import { Box, Typography, styled } from "@mui/material";
import type { DoctorBaseAttributes, PatientBaseAttributes } from "../../../interfaces";
import {
  PrescriptionDocumentType,
  PrescriptionStatus,
  RequestType,
} from "../../../interfaces";
import { CardInfoText, CardSectionSubtitle, GappedCardContainer } from "../styles";
import { ObscuredText } from "@components";
import { differenceInDays, format as formatDate } from "date-fns";
import { format as formatRut } from "rut.js";
import { separateDoctorSpecialty } from "@utils";

const MAX_PRESCRIPTION_AGE_DAYS = 30;

type Props = {
  emissionDate: Date;
  prescriptionType: PrescriptionDocumentType | RequestType.COMPOUNDED_PRESCRIPTION;
  prescriptionStatus: PrescriptionStatus;
  doctorData: Omit<DoctorBaseAttributes, "birthdate">;
  patientData: Omit<PatientBaseAttributes, "email" | "phone">;
  showTitle: boolean;
};

const DateContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-start",
  gap: "4px",
});

const ExpirationWarningText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 500,
  color: theme.palette.error.main,
}));

export const GeneralInfoCard: React.FC<Props> = ({
  emissionDate,
  prescriptionType,
  prescriptionStatus,
  doctorData,
  patientData,
  showTitle,
}) => {
  const { mekidocAccessTitle, specialties } = separateDoctorSpecialty(
    doctorData.speciality,
  );

  const isExpirable =
    prescriptionType === PrescriptionDocumentType.HELD ||
    prescriptionType === RequestType.COMPOUNDED_PRESCRIPTION;

  const isAlreadyHeld = prescriptionStatus === PrescriptionStatus.HELD;

  const isExpired =
    isExpirable &&
    !isAlreadyHeld &&
    differenceInDays(new Date(), emissionDate) > MAX_PRESCRIPTION_AGE_DAYS;

  return (
    <GappedCardContainer>
      {showTitle && <CardSectionSubtitle>Información del documento</CardSectionSubtitle>}
      <Box>
        <DateContainer>
          <CardSectionSubtitle>Fecha emisión: </CardSectionSubtitle>
          <CardInfoText paddingTop={"0px"}>
            {formatDate(emissionDate, "dd/MM/yyyy")}
          </CardInfoText>
        </DateContainer>
        {isExpired && (
          <ExpirationWarningText>
            *Receta emitida hace más de 30 días
          </ExpirationWarningText>
        )}
      </Box>

      <Box>
        <CardSectionSubtitle>Datos doctor</CardSectionSubtitle>
        <CardInfoText>
          {doctorData.name} {doctorData.lastName}
        </CardInfoText>

        <CardInfoText>{formatRut(doctorData.rut)}</CardInfoText>

        <CardInfoText>
          {mekidocAccessTitle} (REG-SIS N°: {doctorData.registrationNumber})
        </CardInfoText>
        {specialties && <CardInfoText>{specialties}</CardInfoText>}
      </Box>

      <Box>
        <CardSectionSubtitle>Datos paciente</CardSectionSubtitle>

        <ObscuredText component={CardInfoText}>
          {patientData.name} {patientData.firstSurname} {patientData.secondSurname}
        </ObscuredText>

        {patientData.rut && (
          <ObscuredText component={CardInfoText}>
            {formatRut(patientData.rut)}
          </ObscuredText>
        )}

        {patientData.passport && (
          <ObscuredText component={CardInfoText}>{patientData.passport}</ObscuredText>
        )}
      </Box>
    </GappedCardContainer>
  );
};
